import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Card } from "../components/ui/card";
import { Heart, Building2, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

// Bank Account Details
const BANK_DETAILS = {
  bankName: "Jaiz Bank",
  accountNumber: "0003415780",
  accountName: "The Companions Foundation"
};

const DonationSection = () => {
  const [packages, setPackages] = useState({});
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/donations/packages`);
        setPackages(response.data);
      } catch (error) {
        console.error("Error fetching packages:", error);
      }
    };
    fetchPackages();
  }, []);

  const copyAccountNumber = () => {
    navigator.clipboard.writeText(BANK_DETAILS.accountNumber);
    setCopied(true);
    toast.success("Account number copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG').format(amount);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary-900 to-primary-800 text-white" data-testid="donation-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Text */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-sm mb-6">
              <Heart className="w-4 h-4" />
              Support Our Cause
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Make a Donation
            </h2>
            <p className="text-primary-100 text-lg mb-6 leading-relaxed">
              Your generous contribution helps us continue our mission of serving humanity. 
              Every donation makes a difference in the lives of those we serve.
            </p>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-sm text-primary-200 italic">
                "The example of those who spend their wealth in the way of Allah is like a seed 
                which grows seven ears, in each ear is a hundred grains." - Quran 2:261
              </p>
            </div>
          </div>

          {/* Right - Bank Transfer Card */}
          <Card className="bg-white text-gray-900 p-6 md:p-8 rounded-2xl shadow-xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary-900" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold">Bank Transfer</h3>
                <p className="text-sm text-gray-500">Quick and easy donation</p>
              </div>
            </div>

            {/* Bank Details */}
            <div className="bg-primary-50 rounded-xl p-6 border-2 border-primary-100 mb-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-primary-100">
                  <span className="text-gray-600 text-sm">Bank Name</span>
                  <span className="font-semibold text-gray-900">{BANK_DETAILS.bankName}</span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-primary-100">
                  <span className="text-gray-600 text-sm">Account Number</span>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 font-mono text-lg">{BANK_DETAILS.accountNumber}</span>
                    <button 
                      onClick={copyAccountNumber}
                      className="p-2 rounded-lg bg-primary-100 hover:bg-primary-200 transition-colors"
                      data-testid="copy-account-btn"
                    >
                      {copied ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-primary-900" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 text-sm">Account Name</span>
                  <span className="font-semibold text-gray-900">{BANK_DETAILS.accountName}</span>
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After making a transfer, please send your payment confirmation 
                to our WhatsApp or email for acknowledgment.
              </p>
            </div>

            {/* Suggested Amounts */}
            <div>
              <p className="text-sm text-gray-600 mb-3">Suggested donation amounts:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(packages).map(([id, pkg]) => (
                  <span 
                    key={id}
                    className="px-4 py-2 rounded-full bg-primary-100 text-primary-900 text-sm font-semibold"
                    data-testid={`suggested-amount-${id}`}
                  >
                    ₦{formatAmount(pkg.amount)}
                  </span>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;
